import { InstancedMesh, Matrix4, Mesh, Vector3 } from '@technology/web3d';
import type { ClipStateTracker } from '@trimble/web3d-utils';
import type { PathSegment } from './station-visual-creator';
import type { PathInstanceData } from './instanced-station-path-mesh';

export interface PathClipApplierDeps {
  readonly tracker: ClipStateTracker;
  /** World-space position of the station at `index`, or `undefined` if missing. */
  getStationPosition(index: number): Vector3 | undefined;
}

/**
 * Applies the active user clip volume to the three station-path renderings
 * (non-instanced segments, hybrid near meshes, instanced far path).
 *
 * For the instanced far path, hidden instances get a zero-scale matrix; the
 * pre-clip matrix is cached so distance-based scaling can be restored when
 * the segment re-enters the clip volume.
 *
 * Slots shadowed by a near mesh are owned by the LOD pipeline (its
 * `hide`/`showInstancedSegment` methods write the matrix). The applier
 * skips them and clears any prior bookkeeping so that — when the slot
 * later becomes the applier's responsibility again — it caches the fresh
 * matrix written by `showInstancedSegment` rather than a stale snapshot.
 */
export class PathClipApplier {
  private static readonly ZERO_SCALE_MATRIX: Matrix4 = new Matrix4().makeScale(
    0,
    0,
    0
  );
  private readonly cachedMatrices: (Matrix4 | undefined)[] = [];
  private clipped: boolean[] = [];

  constructor(private readonly deps: PathClipApplierDeps) {}

  public reset(instanceCount: number): void {
    this.clipped = new Array(instanceCount).fill(false);
    this.cachedMatrices.length = 0;
  }

  public isInstanceClipped(index: number): boolean {
    return this.clipped[index] === true;
  }

  public applyToSegments(segments: readonly PathSegment[]): boolean {
    let changed = false;
    for (const segment of segments) {
      const visible = this.isSegmentVisible(
        segment.startStationIndex,
        segment.endStationIndex
      );
      if (visible !== undefined && segment.mesh.visible !== visible) {
        segment.mesh.visible = visible;
        changed = true;
      }
    }
    return changed;
  }

  public applyToNearMeshes(
    nearMeshes: ReadonlyMap<number, Mesh>,
    farPathData: readonly PathInstanceData[]
  ): boolean {
    let changed = false;
    for (const [segmentIndex, mesh] of nearMeshes) {
      const data = farPathData[segmentIndex];
      if (!data) continue;
      const visible = this.isSegmentVisible(
        data.startStationIndex,
        data.endStationIndex
      );
      if (visible !== undefined && mesh.visible !== visible) {
        mesh.visible = visible;
        changed = true;
      }
    }
    return changed;
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity -- SonarJS trial (NUMARK-1243): complexity 20 > 15 allowed; flagged for later refactor, not fixed here
  public applyToInstancedFarPath(
    farPathMesh: InstancedMesh,
    farPathData: readonly PathInstanceData[],
    nearMeshes: ReadonlyMap<number, Mesh>
  ): boolean {
    let dirty = false;
    for (let i = 0; i < farPathData.length; i++) {
      // Slot is shadowed by a near mesh — visibility is handled by
      // `applyToNearMeshes` and the matrix is owned by the LOD pipeline.
      // Drop our bookkeeping so the next non-shadowed pass captures a
      // fresh matrix instead of restoring a stale cache.
      if (nearMeshes.has(i)) {
        if (this.clipped[i] || this.cachedMatrices[i] !== undefined) {
          this.clipped[i] = false;
          this.cachedMatrices[i] = undefined;
        }
        continue;
      }

      const data = farPathData[i];
      const visible = this.isSegmentVisible(
        data.startStationIndex,
        data.endStationIndex
      );
      if (visible === undefined) continue;

      const wasClipped = this.clipped[i] === true;
      const isClipped = !visible;
      if (isClipped === wasClipped) continue;

      if (isClipped) {
        let cache = this.cachedMatrices[i];
        if (!cache) {
          cache = new Matrix4();
          this.cachedMatrices[i] = cache;
        }
        farPathMesh.getMatrixAt(i, cache);
        farPathMesh.setMatrixAt(i, PathClipApplier.ZERO_SCALE_MATRIX);
      } else {
        const cache = this.cachedMatrices[i];
        if (cache) farPathMesh.setMatrixAt(i, cache);
      }

      this.clipped[i] = isClipped;
      dirty = true;
    }
    if (dirty) farPathMesh.instanceMatrix.needsUpdate = true;
    return dirty;
  }

  private isSegmentVisible(
    startIndex: number,
    endIndex: number
  ): boolean | undefined {
    const start = this.deps.getStationPosition(startIndex);
    const end = this.deps.getStationPosition(endIndex);
    if (!start || !end) return undefined;
    return this.deps.tracker.isSegmentVisibleInActiveClip(start, end);
  }
}
